import * as React from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useFieldControl } from "./Field";

type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "size"> & {
  size?: "sm" | "md";
  /** Visible label to the right of the box. Omit it and supply `aria-label`. */
  children?: React.ReactNode;
};

/** Square checkbox: hairline when unchecked, indigo fill + white tick when
 *  checked. The tick is a real element coloured by `currentColor` (transparent
 *  until checked) so it stays token-driven instead of a hex-baked data URI. */
export const Checkbox = React.forwardRef<HTMLInputElement, Props>(
  (
    { size = "md", className, children, disabled, id, "aria-describedby": describedBy, ...props },
    ref,
  ) => {
    const f = useFieldControl({ id, describedBy });

    return (
      <label
        className={cn(
          "vb-check",
          size === "sm" && "vb-check--sm",
          disabled && "vb-check--disabled",
          className,
        )}
      >
        <input
          ref={ref}
          type="checkbox"
          className="vb-check__input"
          id={f.id}
          aria-describedby={f.describedBy}
          aria-invalid={f.invalid || undefined}
          disabled={disabled}
          {...props}
        />
        <span className="vb-check__box" aria-hidden="true">
          <Check size={size === "sm" ? 11 : 12} strokeWidth={3} />
        </span>
        {children && <span className="vb-check__label">{children}</span>}
      </label>
    );
  },
);
Checkbox.displayName = "Checkbox";

export default Checkbox;
